import { Pressable, Text, TextInput, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Head from 'expo-router/head'
import { StatusBar } from 'expo-status-bar'
import { Button } from '@/src/components/ui/Button'
import { KeyboardAwareScrollLayout } from '@/src/components/ui/KeyboardAwareScrollLayout'
import { colors } from '@/src/theme'
import { body4, label1, label2 } from '@/src/theme/typography'
import { useTranslation } from 'react-i18next'

const NON_SCALING_TEXT_PROPS = {
  allowFontScaling: false,
  maxFontSizeMultiplier: 1,
} as const

const CONTROL_BORDER_RADIUS = 8
const SECTION_GAP = 8

interface VoiceAssessmentPersonalReflectionStepProps {
  personalReflection: string
  onPersonalReflectionChange: (value: string) => void
  onComplete: () => void
  onSkip: () => void
  isSubmitting: boolean
  progressCurrentStep: number
  progressTotalSteps: number
  stepHeaderLabel: string
}

export function VoiceAssessmentPersonalReflectionStep({
  personalReflection,
  onPersonalReflectionChange,
  onComplete,
  onSkip,
  isSubmitting,
  progressCurrentStep,
  progressTotalSteps,
  stepHeaderLabel,
}: VoiceAssessmentPersonalReflectionStepProps) {
  const { t } = useTranslation(['onboarding', 'common'])
  const insets = useSafeAreaInsets()
  const footerPaddingBottom = Math.max(insets.bottom, 8)
  const assessmentProgressSteps = Array.from(
    { length: progressTotalSteps },
    (_, index) => index + 1,
  )

  return (
    <View
      className="bg-background-primary flex-1"
      style={{
        flex: 1,
        backgroundColor: colors.backgroundPrimary,
        paddingTop: insets.top,
      }}
    >
      <Head>
        <title>{t('common:page_title_onboarding')}</title>
      </Head>
      <StatusBar style="dark" />
      <View className="px-6 pt-6 pb-6">
        <View className="flex-row items-center justify-between mb-2 gap-2">
          <Text
            {...NON_SCALING_TEXT_PROPS}
            numberOfLines={2}
            style={[label2, { color: colors.textMuted, flex: 1 }]}
          >
            {stepHeaderLabel}
          </Text>
          <Pressable onPress={onSkip} className="active:opacity-60">
            <Text
              {...NON_SCALING_TEXT_PROPS}
              style={[label2, { color: colors.textBody }]}
            >
              {t('onboarding:voice_assessment.skip_header')}
            </Text>
          </Pressable>
        </View>
        <View className="flex-row items-center gap-2">
          {assessmentProgressSteps.map((stepNumber) => (
            <View
              key={`assessment-progress-${stepNumber}`}
              className={`flex-1 rounded-full ${stepNumber <= progressCurrentStep ? 'bg-primary' : 'bg-border'}`}
              style={{ height: 9 }}
            />
          ))}
        </View>
      </View>

      <KeyboardAwareScrollLayout
        contentInsetAdjustmentBehavior="automatic"
        contentContainerStyle={{
          flexGrow: 1,
          paddingHorizontal: 24,
          paddingTop: 8,
          paddingBottom: 24,
        }}
        footerStyle={{
          paddingHorizontal: 24,
          paddingBottom: footerPaddingBottom,
        }}
        footer={
          <Button
            label={t('onboarding:voice_assessment.complete_assessment_cta')}
            onPress={onComplete}
            variant="primary"
            size="md"
            loading={isSubmitting}
            disabled={isSubmitting}
            containerClassName="w-full"
          />
        }
      >
        <View style={{ gap: SECTION_GAP }}>
          <Text
            {...NON_SCALING_TEXT_PROPS}
            style={[label1, { color: colors.textHeader }]}
          >
            {t('onboarding:reflection.personal_reflection_title')}
          </Text>
          <Text
            {...NON_SCALING_TEXT_PROPS}
            style={[body4, { color: colors.textBody }]}
          >
            {t('onboarding:reflection.personal_reflection_prompt')}
          </Text>
          <TextInput
            value={personalReflection}
            onChangeText={onPersonalReflectionChange}
            multiline
            textAlignVertical="top"
            className="h-[160px] rounded-[8px] border bg-background-secondary px-5 py-4 text-ink text-base"
            style={{
              borderRadius: CONTROL_BORDER_RADIUS,
              borderColor: colors.grayMid,
            }}
            placeholder={t('onboarding:post_payment.notes_placeholder')}
            placeholderTextColor={colors.muted}
          />
        </View>
      </KeyboardAwareScrollLayout>
    </View>
  )
}
